import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, FormField, Input, Select, Box, SpaceBetween, Modal, Icon, Textarea } from '@amzn/awsui-components-core';
import { Avatar, AvatarSize } from '@amzn/stencil-react-components/avatar';
import { RepeatOption, Employee } from '../interfaces/interface';
import { EmployeeService } from '../services/EmployeeService';
import { EmployeeSettingsService } from '../services/EmployeeSettingsService';
import { EAHuddleService } from 'src/services/EAHuddleService';
import { EmployeeSearchBar } from '../components/attendees/EmployeeSearchBar';
import { EmployeeListItem } from '../components/attendees/EmployeeListItem';
import { EmployeePopoverCard } from '../components/attendees/EmployeePopoverCard';
import { ParticipantSelector } from '../components/attendees/ParticipantSelector';
import { MeetingAttachments } from '../components/attachments/MeetingAttachments';
import { MEETING_CONSTANTS } from '../constants/EAHuddleConstants';
import { characterCountConstraintMessage } from 'src/utilities/CommonUtilities';
import { logger } from 'src/logger';
import './MeetingFormContainer.scss';

// ── Constants ──────────────────────────────────────────────────────────

const MAX_DURATION_MINUTES = 86400;

const DURATION_OPTIONS = [
  { label: '15 minutes', value: '15' },
  { label: '30 minutes', value: '30' },
  { label: '45 minutes', value: '45' },
  { label: '60 minutes', value: '60' },
  { label: 'Custom', value: 'custom' }
];

const STANDARD_DURATION_VALUES = DURATION_OPTIONS.filter((o) => o.value !== 'custom').map((o) => o.value);

const REPEAT_OPTIONS: Array<{ label: string; value: RepeatOption }> = [
  { label: 'None', value: 'none' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' }
];

const DEFAULT_DURATION = 30;
const DEFAULT_REPEAT: RepeatOption = 'none';

// ── Props ──────────────────────────────────────────────────────────────

interface MeetingMetaDraft {
  subject: string;
  agenda: string;
  duration: number;
  repeat: RepeatOption;
  onBehalfOfLogin: string | null;
}

interface MeetingFormContainerProps {
  organizerAlias: string;
  subject: string;
  onSubjectChange: (value: string) => void;
  agenda: string;
  onAgendaChange: (value: string) => void;
  duration: number;
  onDurationChange: (value: number) => void;
  repeat: RepeatOption;
  onRepeatChange: (value: RepeatOption) => void;
  onBehalfOfLogin: string | null;
  onBehalfOfChange: (login: string | null) => void;
  isRegistered: boolean;
  onRegisterMeeting: (metadata: { subject: string; agenda: string; duration: number; repeat: RepeatOption; onBehalfOfLogin: string | null }) => void;
  isSaving?: boolean;
  isReadOnly?: boolean;
  meetingId: string | null;
  creatorAlias: string | null;
  defaultEditing?: boolean;
  uploadedAttachments: Array<{ fileName: string; s3Uri: string }>;
  onUploadedAttachmentsChange: (attachments: Array<{ fileName: string; s3Uri: string }>) => void;
  pendingOnBehalfOfName?: string | null;
  onPendingOnBehalfOfDismiss?: () => void;
}

// ── Helper: format duration label ─────────────────────────────────────

function formatDuration(minutes: number): string {
  const opt = DURATION_OPTIONS.find((o) => o.value === String(minutes));
  if (opt && opt.value !== 'custom') return opt.label;
  if (minutes < 60) return `${minutes} minutes`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs} hour${hrs !== 1 ? 's' : ''}`;
}

// ── Component ──────────────────────────────────────────────────────────

export const MeetingFormContainer: React.FC<MeetingFormContainerProps> = ({
  organizerAlias,
  subject,
  onSubjectChange,
  agenda,
  onAgendaChange,
  duration,
  onDurationChange,
  repeat,
  onRepeatChange,
  onBehalfOfLogin,
  onBehalfOfChange,
  isRegistered,
  onRegisterMeeting,
  isSaving = false,
  isReadOnly = false,
  meetingId,
  creatorAlias,
  defaultEditing = false,
  uploadedAttachments,
  onUploadedAttachmentsChange,
  pendingOnBehalfOfName,
  onPendingOnBehalfOfDismiss
}) => {
  // ── ALL hooks must be declared before any early return ─────────────

  const [isEditing, setIsEditing] = useState(defaultEditing);

  const isCustomDuration = !STANDARD_DURATION_VALUES.includes(String(duration));
  const [draft, setDraft] = useState<MeetingMetaDraft>({
    subject,
    agenda,
    duration,
    repeat,
    onBehalfOfLogin
  });
  const [draftDurationMode, setDraftDurationMode] = useState<string>(isCustomDuration ? 'custom' : String(duration));
  const [draftCustomDuration, setDraftCustomDuration] = useState<string>(isCustomDuration ? String(duration) : '');
  const [durationError, setDurationError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [draftAttachments, setDraftAttachments] = useState(uploadedAttachments);

  const [showBehalfOfModal, setShowBehalfOfModal] = useState(false);
  const [behalfOfDraftLogin, setBehalfOfDraftLogin] = useState<string | null>(null);
  const [behalfOfDraftEmployee, setBehalfOfDraftEmployee] = useState<Employee | null>(null);

  const [organizerEmployee, setOrganizerEmployee] = useState<Employee | null>(null);
  const [onBehalfOfEmployee, setOnBehalfOfEmployee] = useState<Employee | null>(null);
  const [draftOnBehalfOfEmployee, setDraftOnBehalfOfEmployee] = useState<Employee | null>(null);

  // Sync draft with props when not actively editing (handles async data loads)
  useEffect(() => {
    if (!isEditing && isRegistered) {
      const currentIsCustom = !STANDARD_DURATION_VALUES.includes(String(duration));
      setDraft({ subject, agenda, duration, repeat, onBehalfOfLogin });
      setDraftDurationMode(currentIsCustom ? 'custom' : String(duration));
      setDraftCustomDuration(currentIsCustom ? String(duration) : '');
      setDraftAttachments(uploadedAttachments);
      setHasUnsavedChanges(false);
    }
    if (!isRegistered) {
      setDraftAttachments(uploadedAttachments);
    }
  }, [subject, agenda, duration, repeat, onBehalfOfLogin, uploadedAttachments, isEditing, isRegistered]);

  // Merge externally added attachments (e.g. template upload) into draft while editing
  useEffect(() => {
    if (!isEditing) return;
    setDraftAttachments((prev) => {
      const existingUris = new Set(prev.map((a) => a.s3Uri));
      const newOnes = uploadedAttachments.filter((a) => !existingUris.has(a.s3Uri));
      if (newOnes.length === 0) return prev;
      return [...prev, ...newOnes];
    });
  }, [uploadedAttachments, isEditing]);

  // Sync props into draft from parent (handles template upload setting fields externally)
  const prevPropsRef = useRef({ subject, agenda, duration, repeat, onBehalfOfLogin });
  const parentChangedAgendaRef = useRef(false);
  useEffect(() => {
    const prev = prevPropsRef.current;
    const updates: Partial<MeetingMetaDraft> = {};
    if (subject !== prev.subject) updates.subject = subject;
    if (agenda !== prev.agenda) {
      updates.agenda = agenda;
      parentChangedAgendaRef.current = true;
    }
    if (duration !== prev.duration) updates.duration = duration;
    if (repeat !== prev.repeat) updates.repeat = repeat;
    if (onBehalfOfLogin !== prev.onBehalfOfLogin) updates.onBehalfOfLogin = onBehalfOfLogin;
    if (Object.keys(updates).length > 0) {
      setDraft((d) => ({ ...d, ...updates }));
    }
    prevPropsRef.current = { subject, agenda, duration, repeat, onBehalfOfLogin };
  }, [subject, agenda, duration, repeat, onBehalfOfLogin]);

  useEffect(() => {
    if (!organizerAlias) return;
    EmployeeService.getEmployeeByLogin(organizerAlias)
      .then(setOrganizerEmployee)
      .catch(() => {});
  }, [organizerAlias]);

  useEffect(() => {
    if (!onBehalfOfLogin) {
      setOnBehalfOfEmployee(null);
      return;
    }
    EmployeeService.getEmployeeByLogin(onBehalfOfLogin)
      .then(setOnBehalfOfEmployee)
      .catch(() => setOnBehalfOfEmployee(null));
  }, [onBehalfOfLogin]);

  useEffect(() => {
    if (!draft.onBehalfOfLogin) {
      setDraftOnBehalfOfEmployee(null);
      return;
    }
    EmployeeService.getEmployeeByLogin(draft.onBehalfOfLogin)
      .then(setDraftOnBehalfOfEmployee)
      .catch(() => setDraftOnBehalfOfEmployee(null));
  }, [draft.onBehalfOfLogin]);

  // Auto-commit draft to parent when not registered (no save button)
  useEffect(() => {
    if (isRegistered) {
      parentChangedAgendaRef.current = false;
      return;
    }
    if (draft.subject !== subject) onSubjectChange(draft.subject);
    if (draft.agenda !== agenda && !parentChangedAgendaRef.current) onAgendaChange(draft.agenda);
    parentChangedAgendaRef.current = false;
    if (draft.duration !== duration) onDurationChange(draft.duration);
    if (draft.repeat !== repeat) onRepeatChange(draft.repeat);
    if (draft.onBehalfOfLogin !== onBehalfOfLogin) onBehalfOfChange(draft.onBehalfOfLogin);
  }, [draft, isRegistered]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Callbacks ──────────────────────────────────────────────────────

  const handleStartEdit = useCallback(() => {
    const currentIsCustom = !STANDARD_DURATION_VALUES.includes(String(duration));
    setDraft({ subject, agenda, duration, repeat, onBehalfOfLogin });
    setDraftDurationMode(currentIsCustom ? 'custom' : String(duration));
    setDraftCustomDuration(currentIsCustom ? String(duration) : '');
    setDraftAttachments(uploadedAttachments);
    setDurationError('');
    setHasUnsavedChanges(false);
    setIsEditing(true);
  }, [subject, agenda, duration, repeat, onBehalfOfLogin, uploadedAttachments]);

  const handleDraftSubjectChange = (value: string) => {
    setDraft((prev) => ({ ...prev, subject: value }));
    setHasUnsavedChanges(true);
  };

  const handleDraftDurationSelect = (value: string) => {
    setDraftDurationMode(value);
    setDurationError('');
    if (value !== 'custom') {
      setDraftCustomDuration('');
      setDraft((prev) => ({ ...prev, duration: parseInt(value, 10) }));
      setHasUnsavedChanges(true);
    }
  };

  const handleDraftCustomDurationChange = (value: string) => {
    setDraftCustomDuration(value);
    const num = parseInt(value, 10);
    if (isNaN(num) || num <= 0) {
      setDurationError('Duration must be a positive number');
    } else if (num > MAX_DURATION_MINUTES) {
      setDurationError(`Maximum duration is 60 days (${MAX_DURATION_MINUTES.toLocaleString()} minutes)`);
    } else {
      setDurationError('');
      setDraft((prev) => ({ ...prev, duration: num }));
      setHasUnsavedChanges(true);
    }
  };

  const handleDraftRepeatChange = (value: RepeatOption) => {
    setDraft((prev) => ({ ...prev, repeat: value }));
    setHasUnsavedChanges(true);
  };

  const handleOpenBehalfOfModal = () => {
    setBehalfOfDraftLogin(draft.onBehalfOfLogin);
    setBehalfOfDraftEmployee(draftOnBehalfOfEmployee);
    setShowBehalfOfModal(true);
  };

  const handleBehalfOfEmployeeSelected = (employee: Employee, _priority?: unknown) => {
    setBehalfOfDraftLogin(employee.login_name);
    setBehalfOfDraftEmployee(employee);
  };

  const handleBehalfOfModalConfirm = () => {
    setDraft((prev) => ({ ...prev, onBehalfOfLogin: behalfOfDraftLogin }));
    setDraftOnBehalfOfEmployee(behalfOfDraftEmployee);
    setHasUnsavedChanges(true);
    setShowBehalfOfModal(false);
  };

  const handleBehalfOfModalDismiss = () => {
    setBehalfOfDraftLogin(null);
    setBehalfOfDraftEmployee(null);
    setShowBehalfOfModal(false);
  };

  const handleReadOnlyBehalfOfSelect = useCallback(
    (employee: Employee) => {
      onBehalfOfChange(employee.login_name);
    },
    [onBehalfOfChange]
  );

  const handleSave = useCallback(async () => {
    if (durationError) return;

    if (isRegistered && meetingId && creatorAlias) {
      try {
        await EmployeeSettingsService.updateMeetingMetadata(creatorAlias, meetingId, {
          subject: draft.subject,
          agenda: draft.agenda,
          duration: draft.duration,
          repeat: draft.repeat,
          onBehalfOfLogin: draft.onBehalfOfLogin,
          attachments: draftAttachments.map((a) => a.s3Uri)
        });
        onUploadedAttachmentsChange(draftAttachments);
      } catch (error) {
        logger.error('Failed to save meeting metadata', { error, meetingId });
        return;
      }
    }
    onSubjectChange(draft.subject);
    onAgendaChange(draft.agenda);
    onDurationChange(draft.duration);
    onRepeatChange(draft.repeat);
    onBehalfOfChange(draft.onBehalfOfLogin);
    setHasUnsavedChanges(false);
    setIsEditing(false);
  }, [
    draft,
    draftAttachments,
    durationError,
    onSubjectChange,
    onAgendaChange,
    onDurationChange,
    onRepeatChange,
    onBehalfOfChange,
    onUploadedAttachmentsChange,
    isRegistered,
    meetingId,
    creatorAlias
  ]);

  const handleDiscard = useCallback(() => {
    // Delete S3 objects that were uploaded during this edit session but not yet saved
    const savedUris = new Set(uploadedAttachments.map((a) => a.s3Uri));
    const orphaned = draftAttachments.filter((a) => !savedUris.has(a.s3Uri));
    orphaned.forEach((a) => EAHuddleService.deleteAttachment(a.s3Uri));

    if (isRegistered) {
      const currentIsCustom = !STANDARD_DURATION_VALUES.includes(String(duration));
      setDraft({ subject, agenda, duration, repeat, onBehalfOfLogin });
      setDraftDurationMode(currentIsCustom ? 'custom' : String(duration));
      setDraftCustomDuration(currentIsCustom ? String(duration) : '');
    } else {
      setDraft({ subject: '', agenda: '', duration: DEFAULT_DURATION, repeat: DEFAULT_REPEAT, onBehalfOfLogin: null });
      setDraftDurationMode(String(DEFAULT_DURATION));
      setDraftCustomDuration('');
    }
    setDraftAttachments(uploadedAttachments);
    setDurationError('');
    setHasUnsavedChanges(false);
    setIsEditing(false);
  }, [isRegistered, subject, agenda, duration, repeat, onBehalfOfLogin, uploadedAttachments, draftAttachments]);

  // ── Read-only bar (after all hooks) ────────────────────────────────

  if (isRegistered && !isEditing) {
    return (
      <div className="meeting-meta-form">
        <div className="meeting-meta-readonly-subject">
          <Box fontSize="heading-l" fontWeight="bold">
            {subject || 'Untitled Meeting'}
          </Box>
          {!isReadOnly && (
            <div className="meeting-meta-form-edit-inline">
              <Button variant="primary" iconName="edit" onClick={handleStartEdit}>
                Edit
              </Button>
            </div>
          )}
        </div>
        <div className="meeting-meta-readonly-row">
          <div className="meeting-meta-field--compact">
            <SpaceBetween direction="horizontal" size="xxs">
              <Box fontWeight="bold" variant="span">
                Duration
              </Box>
              <Box variant="span" color="text-body-secondary">
                {formatDuration(duration)}
              </Box>
            </SpaceBetween>
          </div>
          <div className="meeting-meta-readonly-divider" />
          <div className="meeting-meta-field--compact">
            <SpaceBetween direction="horizontal" size="xxs">
              <Box fontWeight="bold" variant="span">
                Recurrence
              </Box>
              <Box variant="span" color="text-body-secondary">
                {REPEAT_OPTIONS.find((o) => o.value === repeat)?.label ?? 'None'}
              </Box>
            </SpaceBetween>
          </div>
          <div className="meeting-meta-readonly-divider" />
          <div className="meeting-meta-field--compact">
            <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
              <Box fontWeight="bold" variant="span">
                Organizer
              </Box>
              {organizerEmployee ? (
                <>
                  <EmployeePopoverCard loginName={organizerAlias} renderWithPortal>
                    <Avatar username={organizerAlias} size={AvatarSize.ExtraSmall} imageAltText={`Profile image of ${organizerAlias}`} />
                  </EmployeePopoverCard>
                  <Box fontSize="body-s" fontWeight="bold">
                    {organizerEmployee.employee_name || organizerAlias}
                  </Box>
                </>
              ) : null}
            </SpaceBetween>
          </div>
          <div className="meeting-meta-readonly-divider" />
          <div className="meeting-meta-field--compact">
            <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
              <Box fontWeight="bold" variant="span">
                Behalf of
              </Box>
              {onBehalfOfEmployee ? (
                <>
                  <EmployeePopoverCard loginName={onBehalfOfEmployee.login_name} renderWithPortal>
                    <Avatar
                      username={onBehalfOfEmployee.login_name}
                      size={AvatarSize.ExtraSmall}
                      imageAltText={`Profile image of ${onBehalfOfEmployee.login_name}`}
                    />
                  </EmployeePopoverCard>
                  <Box fontSize="body-s" fontWeight="bold">
                    {onBehalfOfEmployee.employee_name || onBehalfOfEmployee.login_name}
                  </Box>
                  {!isReadOnly && <Button variant="icon" iconName="close" ariaLabel="Clear on behalf of" onClick={() => onBehalfOfChange(null)} />}
                </>
              ) : pendingOnBehalfOfName && !isReadOnly ? (
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  <ParticipantSelector
                    onEmployeeSelect={handleReadOnlyBehalfOfSelect}
                    placeholder={`Search for "${pendingOnBehalfOfName}"...`}
                    initialQuery={pendingOnBehalfOfName}
                    buttonText="Select Participant"
                  />
                  {onPendingOnBehalfOfDismiss && (
                    <Button variant="icon" iconName="close" ariaLabel="Dismiss on behalf of" onClick={onPendingOnBehalfOfDismiss} />
                  )}
                </SpaceBetween>
              ) : (
                <Box variant="span" color="text-body-secondary">
                  —
                </Box>
              )}
            </SpaceBetween>
          </div>
        </div>
        {agenda && (
          <div className="meeting-meta-agenda-row">
            <Box fontSize="body-m" color="text-status-inactive">
              {agenda}
            </Box>
          </div>
        )}
        {uploadedAttachments.length > 0 && (
          <div className="meeting-meta-attachments-section">
            <Box fontWeight="bold" variant="span">
              Attachments
            </Box>
            <div className="meeting-meta-bar-attachments">
              {uploadedAttachments.map((file) => (
                <div key={file.s3Uri} className="meeting-meta-bar-attachment-item">
                  <span className="meeting-meta-bar-attachment-icon">
                    <Icon name="file" size="small" />
                  </span>
                  <span
                    className="meeting-meta-bar-attachment-link"
                    role="button"
                    tabIndex={0}
                    onClick={() => EAHuddleService.downloadAttachment(file.s3Uri)}
                    onKeyDown={(e) => e.key === 'Enter' && EAHuddleService.downloadAttachment(file.s3Uri)}
                  >
                    {file.fileName}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Edit / pre-registration form ───────────────────────────────────

  return (
    <div className="meeting-meta-form">
      <div className="meeting-meta-edit-subject">
        <FormField
          label="Subject"
          stretch
          constraintText={characterCountConstraintMessage(MEETING_CONSTANTS.MAX_SUBJECT_LENGTH, draft.subject.length)}
        >
          <Input
            value={draft.subject}
            onChange={({ detail }) => handleDraftSubjectChange(detail.value.slice(0, MEETING_CONSTANTS.MAX_SUBJECT_LENGTH))}
            placeholder="Untitled Meeting"
            disabled={isReadOnly}
          />
        </FormField>
      </div>
      <div className="meeting-meta-edit-top-row">
        <div className="meeting-meta-field--compact">
          <FormField label="Duration">
            <SpaceBetween direction="vertical" size="xs">
              <Select
                selectedOption={DURATION_OPTIONS.find((o) => o.value === draftDurationMode) ?? DURATION_OPTIONS[1]}
                onChange={({ detail }) => handleDraftDurationSelect(detail.selectedOption.value ?? '30')}
                options={DURATION_OPTIONS}
                disabled={isReadOnly}
              />
              {draftDurationMode === 'custom' && (
                <FormField errorText={durationError} stretch={false}>
                  <Input
                    value={draftCustomDuration}
                    onChange={({ detail }) => handleDraftCustomDurationChange(detail.value)}
                    placeholder="Minutes"
                    type="number"
                    disabled={isReadOnly}
                  />
                </FormField>
              )}
            </SpaceBetween>
          </FormField>
        </div>
        <div className="meeting-meta-readonly-divider" />
        <div className="meeting-meta-field--compact">
          <FormField label="Recurrence">
            <Select
              selectedOption={REPEAT_OPTIONS.find((o) => o.value === draft.repeat) ?? REPEAT_OPTIONS[0]}
              onChange={({ detail }) => handleDraftRepeatChange((detail.selectedOption.value as RepeatOption) ?? 'none')}
              options={REPEAT_OPTIONS}
              disabled={isReadOnly}
            />
          </FormField>
        </div>
        <div className="meeting-meta-readonly-divider" />
        <div className="meeting-meta-field--compact">
          <FormField label="Organizer">
            <div className="meeting-meta-sidebar-person">
              {organizerEmployee && (
                <EmployeePopoverCard loginName={organizerAlias} renderWithPortal>
                  <Avatar username={organizerAlias} size={AvatarSize.ExtraSmall} imageAltText={`Profile image of ${organizerAlias}`} />
                </EmployeePopoverCard>
              )}
              <Box fontSize="body-s" fontWeight="bold">
                {organizerEmployee?.employee_name || organizerAlias}
              </Box>
            </div>
          </FormField>
        </div>
        <div className="meeting-meta-readonly-divider" />
        <div className="meeting-meta-field--compact">
          <FormField label="Behalf of">
            {draft.onBehalfOfLogin ? (
              <div className="meeting-meta-sidebar-person">
                <EmployeePopoverCard loginName={draft.onBehalfOfLogin} renderWithPortal>
                  <Avatar username={draft.onBehalfOfLogin} size={AvatarSize.ExtraSmall} imageAltText={`Profile image of ${draft.onBehalfOfLogin}`} />
                </EmployeePopoverCard>
                <Box fontSize="body-s" fontWeight="bold">
                  {draftOnBehalfOfEmployee?.employee_name ?? draft.onBehalfOfLogin}
                </Box>
                {!isReadOnly && (
                  <button
                    className="edit-icon-button"
                    onClick={handleOpenBehalfOfModal}
                    aria-label="Change on behalf of"
                    type="button"
                    title="Change on behalf of"
                  >
                    <Icon name="edit" />
                  </button>
                )}
                {!isReadOnly && (
                  <Button
                    variant="icon"
                    iconName="close"
                    ariaLabel="Clear on behalf of"
                    onClick={() => {
                      setDraft((prev) => ({ ...prev, onBehalfOfLogin: null }));
                      setHasUnsavedChanges(true);
                    }}
                  />
                )}
              </div>
            ) : pendingOnBehalfOfName && !isReadOnly ? (
              <div className="meeting-meta-sidebar-person">
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  <ParticipantSelector
                    onEmployeeSelect={(employee) => {
                      setDraft((prev) => ({ ...prev, onBehalfOfLogin: employee.login_name }));
                      setHasUnsavedChanges(true);
                    }}
                    placeholder={`Search for "${pendingOnBehalfOfName}"...`}
                    initialQuery={pendingOnBehalfOfName}
                    buttonText="Select Participant"
                  />
                  {onPendingOnBehalfOfDismiss && (
                    <Button variant="icon" iconName="close" ariaLabel="Dismiss on behalf of" onClick={onPendingOnBehalfOfDismiss} />
                  )}
                </SpaceBetween>
              </div>
            ) : (
              <div className="meeting-meta-sidebar-person">
                <Box fontSize="body-s" color="text-status-inactive">
                  Not selected
                </Box>
                {!isReadOnly && <Button variant="icon" iconName="add-plus" ariaLabel="Select on behalf of" onClick={handleOpenBehalfOfModal} />}
              </div>
            )}
          </FormField>
        </div>
      </div>

      <div className="meeting-meta-edit-body">
        <FormField label="Agenda" stretch constraintText={characterCountConstraintMessage(MEETING_CONSTANTS.MAX_AGENDA_LENGTH, draft.agenda.length)}>
          <Textarea
            value={draft.agenda}
            onChange={({ detail }) => {
              setDraft((prev) => ({ ...prev, agenda: detail.value.slice(0, MEETING_CONSTANTS.MAX_AGENDA_LENGTH) }));
              setHasUnsavedChanges(true);
            }}
            placeholder="Enter meeting agenda..."
            disabled={isReadOnly}
            rows={4}
          />
        </FormField>
      </div>

      {(isRegistered || draftAttachments.length > 0) && (
        <div className="meeting-meta-attachments-section">
          <MeetingAttachments
            attachments={draftAttachments}
            onAttachmentsChange={(updated) => {
              setDraftAttachments(updated);
              setHasUnsavedChanges(true);
            }}
            meetingId={meetingId}
            organizerAlias={organizerAlias}
            isReadOnly={isReadOnly || !isRegistered}
          />
        </div>
      )}

      {!isReadOnly && isRegistered && (
        <div className="meeting-meta-form-actions">
          <Button variant="link" onClick={handleDiscard} disabled={isSaving}>
            Discard
          </Button>
          <span className="blue-button">
            <Button variant="primary" onClick={handleSave} loading={isSaving} disabled={isSaving || !hasUnsavedChanges || !!durationError}>
              Save
            </Button>
          </span>
        </div>
      )}

      {showBehalfOfModal && (
        <Modal
          visible={true}
          onDismiss={handleBehalfOfModalDismiss}
          header="Select on Behalf of"
          size="medium"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={handleBehalfOfModalDismiss}>
                  Cancel
                </Button>
                <span className="blue-button">
                  <Button variant="primary" onClick={handleBehalfOfModalConfirm} disabled={!behalfOfDraftLogin}>
                    Select
                  </Button>
                </span>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween direction="vertical" size="m">
            <Box fontSize="body-s" color="text-body-secondary">
              Choose who you are scheduling this meeting on behalf of:
            </Box>
            <div className="meeting-meta-behalf-modal">
              <EmployeeSearchBar
                onAddAttendee={handleBehalfOfEmployeeSelected}
                existingEmails={new Set()}
                singleAddButton={true}
                addButtonText="Select"
                placeholder="Search for an employee..."
                fullWidth={true}
              />
            </div>
            {behalfOfDraftEmployee && <EmployeeListItem employee={behalfOfDraftEmployee} disableHover />}
          </SpaceBetween>
        </Modal>
      )}
    </div>
  );
};
